import { writable, get as getStore } from "svelte/store";
import user from '@/store/user.js'
import app from "@/store/app.js";
import { currencies } from '@/assets/js/options.js'
import config from "@/config"
import { discounts, BUNDLE_PRICE_RANGE, BUNDLE_MIN_GRAMS, PREROLL_BUNDLE_MAX_GRAMS, ROLLING_SERVICE_FEE } from "@/assets/js/options";
import { toasts } from "svelte-toasts";
import { onDestroy } from 'svelte';

export function uuid() {
    const c = typeof crypto !== "undefined" ? crypto : undefined;
    if (c?.randomUUID) return c.randomUUID();
    if (c?.getRandomValues) {
        const b = c.getRandomValues(new Uint8Array(16));
        b[6] = (b[6] & 0x0f) | 0x40;
        b[8] = (b[8] & 0x3f) | 0x80;
        const h = [...b].map((x) => x.toString(16).padStart(2, "0"));
        return `${h[0]}${h[1]}${h[2]}${h[3]}-${h[4]}${h[5]}-${h[6]}${h[7]}-${h[8]}${h[9]}-${h[10]}${h[11]}${h[12]}${h[13]}${h[14]}${h[15]}`;
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
        const r = (Math.random() * 16) | 0;
        const v = ch === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/**
 * Compute the next unique barcode for a collection.
 *
 * Takes one above the highest barcode already issued (never the row count, so
 * deletions never reuse an old barcode), then VERIFIES the candidate against
 * every existing barcode — including ones outside the expected prefix/format
 * and any not-yet-saved barcodes in `reserved` — bumping until it is truly free.
 *
 * @param {Array} records   existing docs, each with a `barcode` field
 * @param {Object} opts
 * @param {string} opts.prefix   leading digit for this collection ("1" inventory, "2" strains)
 * @param {number} opts.floor    fallback base when no barcode matches the prefix
 * @param {Array}  opts.reserved barcodes claimed this session but not yet persisted
 * @returns {number} a barcode guaranteed not to collide with any known barcode
 */
export function nextUniqueBarcode(records = [], { prefix, floor = 1000000, reserved = [] } = {}) {
    const re = prefix ? new RegExp(`^${prefix}\\d{6}$`) : null;

    // Every barcode already in use, in any format — the set we must not collide with.
    const taken = new Set(
        [...records.map((r) => r?.barcode), ...reserved]
            .map((b) => Number(b))
            .filter((n) => Number.isFinite(n))
    );

    // Highest barcode that matches this collection's expected prefix/format.
    const matching = [...taken].filter((n) => !re || re.test(String(n)));
    let candidate = (matching.length ? Math.max(...matching) : floor) + 1;

    // Verify: keep bumping until the candidate is genuinely unused.
    while (taken.has(candidate)) candidate++;
    return candidate;
}

export function clickOutside(element, callbackFunction) {
    function onClick(event) {
        // Ignore clicks whose target (or our own element) has been detached from
        // the DOM by a re-render in the same tick — a rebuilt row is not a real
        // outside click, and treating it as one wrongly closes menus/panels.
        if (!element.isConnected || !event.target?.isConnected) return;
        if (!element.contains(event.target)) {
            callbackFunction();
        }
    }
    document.body.addEventListener("click", onClick);
    return {
        update(newCallbackFunction) {
            callbackFunction = newCallbackFunction;
        },
        destroy() {
            document.body.removeEventListener("click", onClick);
        },
    };
}

export function copy(data) {
    return JSON.parse(JSON.stringify(data))
}

export function set(name, type) {
    const value = writable(localStorage.getItem(name) ? JSON.parse(localStorage.getItem(name)) : type);
    value.subscribe(val => { localStorage.setItem(name, JSON.stringify(val)) });
    return value;
}

// `exclude` keys live in memory only — use it for large data that is refetched
// on load anyway, so every small change doesn't rewrite it to localStorage.
export function persist(name, data, { exclude = [] } = {}) {
    let local = localStorage.getItem(name);
    const value = writable(local ? JSON.parse(local) : data);

    if (!local) value.set(copy(data));

    value.subscribe(val => {
        if (!exclude.length) return localStorage.setItem(name, JSON.stringify(val));
        const saved = { ...val };
        for (const key of exclude) delete saved[key];
        localStorage.setItem(name, JSON.stringify(saved));
    });

    // Reset store to initial data
    value.reset = () => value.set(copy(data));

    // Get the value of a specific key
    value.val = (key) => getStore(value)[key];

    // Get the entire store object
    value.all = () => getStore(value);

    // Commit a value to a specific key
    value.commit = (key, val) => {
        let temp = getStore(value);
        temp[key] = val != null ? val : data[key];
        value.set(temp);
        return temp;
    };

    // Clear a specific key (reset to initial value)
    value.clear = (key) => {
        let temp = getStore(value);
        temp[key] = data[key];
        value.set(temp);
        return temp;
    };

    // Initialize missing keys from default data
    for (let x of Object.keys(data)) {
        if (!Object.keys(JSON.parse(local) || {}).includes(x)) {
            let temp = getStore(value);
            temp[x] = data[x];
            value.set(temp);
        }
    }

    value.watch = (keys, callback) => {
        const isArray = Array.isArray(keys)
        let previous = isArray ? keys.map(k => getStore(value)[k]) : getStore(value)[keys]

        const unsub = value.subscribe(current => {
            const currentValues = isArray ? keys.map(k => current[k]) : current[keys]
            const changed = isArray ? currentValues.some((v, i) => v !== previous[i]) : currentValues !== previous

            if (changed) {
                if (isArray) {
                    previous = currentValues
                    callback(currentValues)
                } else {
                    const oldVal = previous
                    const newVal = currentValues
                    previous = newVal
                    callback(oldVal, newVal)
                }
            }
        })

        onDestroy(unsub)
        return unsub
    }


    return value;
}


export function formatCurrency(x) {
    let active = getStore(user)?.currency;
    active = active ? active : 'THB';
    let currency = currencies.filter((el) => el.id == active)[0];
    x = isNaN(x) ? 0 : x;
    x = x * currency.convert;
    x = Number(x).toFixed(currency.sign);
    var parts = x.toString().split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    return currency.symbol + " " + parts.join(".");
}

export function formatCurrencyWithoutSymbol(x) {
    let active = getStore(user)?.currency;
    active = active ? active : 'THB';
    let currency = currencies.filter((el) => el.id == active)[0];
    x = isNaN(x) ? 0 : x;
    x = x * currency.convert;
    x = Number(x).toFixed(currency.sign);
    var parts = x.toString().split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    return parts.join(".");
}

export function formatSymbol(x) {
    let active = getStore(user)?.currency;
    active = active ? active : 'THB';
    let currency = currencies.filter((el) => el.id == active)[0];
    x = isNaN(x) ? 0 : x;
    x = x * currency.convert;
    x = Number(x).toFixed(currency.sign);
    var parts = x.toString().split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    return currency.symbol;
}

// A unique per-cart-line key. Cart lines can share a product id (a duplicated
// line copies everything, barcode included), so id alone can't tell two lines
// apart — this keys the active/scale line and the "on scale" highlight.
export function newCartKey() {
    try { if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID(); } catch (_) {}
    return `k${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

// The code printed in a label's QR (junglehouse.co.th/l/<code>) and stored on the
// sale line, so a scan can be traced to its order. Derived from the line's
// cartKey — stable across reprints with no extra state — and cut to its last 10
// chars (the random tail of the UUID) so the QR stays a small v3 code.
export function labelCodeOf(item) {
    const k = String(item?.cartKey || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    return k.length >= 10 ? k.slice(-10) : "";
}

export function getPercentForGrams(grams) {
    for (let i = discounts.length - 1; i >= 0; i--) {
        if (grams >= discounts[i].grams) {
            return discounts[i].percent;
        }
    }
    return discounts[0].percent;
}

// Grams a price group counts toward the bundle ladder. Prerolls only add up to
// PREROLL_BUNDLE_MAX_GRAMS, so 20 joints top out at the 5g tier, while
// 5g flower + 2 prerolls still reaches 7g.
export function bundleGrams(group) {
    let flower = 0, preroll = 0;
    for (const item of group) {
        const grams = Number(item.weight || item.quantity || 0);
        if (item.preroll) preroll += grams;
        else flower += grams;
    }
    return flower + Math.min(preroll, PREROLL_BUNDLE_MAX_GRAMS);
}

// A line's share of its group's bundle price. Prerolls never go below the
// PREROLL_BUNDLE_MAX_GRAMS tier (10% off), even inside a bigger flower bundle.
export function linePercent(item, groupPercent) {
    return item.preroll ? Math.max(groupPercent, getPercentForGrams(PREROLL_BUNDLE_MAX_GRAMS)) : groupPercent;
}

// Flat rolling-service charge on a line. A service, not product — it's added
// on top of the line price, never discounted, and still charged on free lines.
export function rollingFee(item) {
    return item?.rolling ? ROLLING_SERVICE_FEE : 0;
}

export function groupFlowerByPrice(cart, priceThreshold = BUNDLE_PRICE_RANGE) {
    // Wholesale lines carry a fixed staff-entered total, so they must never
    // join the volume-discount grouping (their price mustn't be re-laddered).
    const flowerItems = cart.filter(item => item.category !== 'inventory' && !item.free && !item.wholesale);
    const inventoryItems = cart.filter(item => item.category === 'inventory');
    // Free flower items are excluded from price grouping/discounts, but they are
    // still flower — render them as ungrouped flower, not as inventory.
    const freeFlowerItems = cart.filter(item => item.category !== 'inventory' && item.free);
    // Wholesale flower renders as its own ungrouped line (fixed price).
    const wholesaleFlowerItems = cart.filter(item => item.category !== 'inventory' && !item.free && item.wholesale);

    if (flowerItems.length === 0) {
        return { groups: [], inventoryItems, ungroupedFlowerItems: [...freeFlowerItems, ...wholesaleFlowerItems] };
    }
    
    const sortedFlower = [...flowerItems].sort((a, b) => {
        const priceA = Number(a.customerPrice || a.price);
        const priceB = Number(b.customerPrice || b.price);
        return priceA - priceB;
    });
    
    const groups = [];
    let currentGroup = [sortedFlower[0]];
    const basePrice = Number(sortedFlower[0].customerPrice || sortedFlower[0].price);
    
    for (let i = 1; i < sortedFlower.length; i++) {
        const itemPrice = Number(sortedFlower[i].customerPrice || sortedFlower[i].price);
        const groupBasePrice = Number(currentGroup[0].customerPrice || currentGroup[0].price);
        
        if (Math.abs(itemPrice - groupBasePrice) <= priceThreshold) {
            currentGroup.push(sortedFlower[i]);
        } else {
            groups.push(currentGroup);
            currentGroup = [sortedFlower[i]];
        }
    }
    
    if (currentGroup.length > 0) {
        groups.push(currentGroup);
    }
    
    const validGroups = [];
    const ungroupedFlowerItems = [];
    for (const group of groups) {
        if (bundleGrams(group) >= BUNDLE_MIN_GRAMS) {
            validGroups.push(group);
        } else {
            ungroupedFlowerItems.push(...group);
        }
    }
    
    ungroupedFlowerItems.push(...freeFlowerItems, ...wholesaleFlowerItems);

    return { groups: validGroups, inventoryItems, ungroupedFlowerItems };
}

export function calculateGroupedTotal(cart) {
    const { groups, inventoryItems, ungroupedFlowerItems } = groupFlowerByPrice(cart);
    
    let total = 0;
    
    for (const group of groups) {
        const discountPercent = getPercentForGrams(bundleGrams(group));
        
        for (const item of group) {
            const price = Number(item.customerPrice || item.price);
            const grams = Number(item.weight || item.quantity || 0);
            const itemDiscount = Number(item.discount || 0);
            const itemTotal = (price * linePercent(item, discountPercent) * grams * (100 - itemDiscount)) / 100;
            total += itemTotal;
        }
    }
    
    for (const item of ungroupedFlowerItems) {
        if (item.free) continue;
        // Wholesale line: staff-entered price PER GRAM × grams, no ladder/discount.
        if (item.wholesale) { total += (Number(item.wholesalePrice) || 0) * (Number(item.weight) || 0); continue; }
        const price = Number(item.customerPrice || item.price);
        const grams = Number(item.weight || item.quantity || 0);
        const itemDiscount = Number(item.discount || 0);
        const itemTotal = (price * grams * (100 - itemDiscount)) / 100;
        total += itemTotal;
    }

    for (const item of inventoryItems) {
        if (item.free) continue;
        if (item.wholesale) { total += (Number(item.wholesalePrice) || 0) * (Number(item.weight) || 0); continue; }
        const price = Number(item.customerPrice || item.price);
        const quantity = Number(item.weight || item.quantity || 0);
        total += price * quantity;
    }

    for (const item of cart) total += rollingFee(item);

    return total;
}

// Round a weighed value DOWN to the nearest 0.5 g — the BILLED weight. The
// customer is charged on this, so we never bill for more grams than physically
// left the jar (the true scale weight is kept separately, in item.actualWeight,
// and is what stock is deducted by). e.g. 3.62 -> 3.5, 3.99 -> 3.5, 4.0 -> 4.0.
export function floorToHalfGram(grams) {
    const g = Number(grams);
    if (!Number.isFinite(g) || g <= 0) return 0;
    return Math.floor(g * 2) / 2;
}

export function price(price, grams, strainDiscount = 0) {
    return formatCurrency((Number(price) * getPercentForGrams(grams) * Number(grams) * (100 - Number(strainDiscount))) / 100)
}

export function priceWithDiscount(price, grams, discountPercent, strainDiscount = 0) {
    return formatCurrency((Number(price) * discountPercent * Number(grams) * (100 - Number(strainDiscount))) / 100)
}

export function truePrice(price, grams, strainDiscount = 0) {
    return ((price * getPercentForGrams(grams) * grams * (100 - strainDiscount)) / 100)
}

export function truePriceWithDiscount(price, grams, discountPercent, strainDiscount = 0) {
    return ((price * discountPercent * grams * (100 - strainDiscount)) / 100)
}

export function priceBeforeDiscount(price, grams, strainDiscount = 0) {
    return formatCurrency((Number(price) * Number(grams) * (100 - Number(strainDiscount))) / 100)
}

export function cacheImage(imagePath) {
    const img = new Image();
    img.src = imagePath;
    return img
}

function getToken() {
    let getUser = localStorage.getItem('user');
    return getUser ? JSON.parse(localStorage.getItem('user')).token : ''
}

let headers = {
    Accept: "application/json",
    token: getToken(),
    "Content-Type": "application/json",
}

export async function postImage(route, data) {
    let url = `${config.API_URL}/${route}`;
    let res = await fetch(url, {
        method: "POST",
        body: data,
        headers: {
            token: getToken(),
        }
    })
    try {
        return await res.json()
    } catch (error) {
        return await res.status
    }
}

export async function deleteImage(route, data) {
    let url = `${config.API_URL}/${route}`;
    let res = await fetch(url, {
        method: "DELETE",
        body: data,
        headers: {
            token: getToken(),
        }
    })
    return res.status
}

export function wait(ms = 0) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export function notify(status, message) {
    let con = status == 0 || status == 'error'
    let mode = con ? "error" : "success";
    let data = { mode, message, lifetime: 2 };
    // acts.add(data);
}

export function tell(res, success, error) {
    let con = res.status == 'ok' || res == 200;
    let mode = con ? "success" : "error";
    let message = con ? success : error;
    let data = { mode, message, lifetime: 2 };
    // acts.add(data);
}

export async function post(route, data) {
    let url = `${config.API_URL}/${route}`;
    let res = await fetch(url, {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
            Accept: "application/json",

            "Content-Type": "application/json",
        }
    })
    try {
        return await res.json()
    } catch (error) {
        return await res.status
    }
}

export async function del(route, data) {
    let url = `${config.API_URL}/${route}`;
    let res = await fetch(url, {
        method: "DELETE",
        body: JSON.stringify(data),
        headers: {
            Accept: "application/json",
            token: getToken(),
            "Content-Type": "application/json",
        }
    })
    try {
        return await res.json()
    } catch (error) {
        return await res.status
    }
}

export async function get(route) {
    let url = `${config.API_URL}/${route}`;
    let res = await fetch(url, {
        method: "GET",
        headers: {
            Accept: "application/json",
            token: getToken(),
            "Content-Type": "application/json",
        }
    })
    res = await res.json()
    if (res.status == 'error') return []
    return res
}

export async function patch(route, data) {
    let url = `${config.API_URL}/${route}`;
    let res = await fetch(url, {
        method: "PATCH",
        headers: {
            Accept: "application/json",
            token: getToken(),
            "Content-Type": "application/json",
        },
        body: JSON.stringify(data)
    })
    try {
        return await res.json()
    } catch (error) {
        return await res.status
    }
}

export const validateEmail = function (email) {
    return /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email)
}

export function push(path) {
    app.commit('url', path)
}

const SHOPPING_RETURN_KEY = 'shoppingReturnUrl';
const CART_FLOW = new Set(['/cart', '/checkout', '/payment', '/confirmation']);

export function stashShoppingReturnUrl(url) {
    if (!url) return;
    try {
        sessionStorage.setItem(SHOPPING_RETURN_KEY, url);
    } catch (_) {}
}

export function getShoppingReturnUrl(fallback = '/products') {
    try {
        return sessionStorage.getItem(SHOPPING_RETURN_KEY) || fallback;
    } catch (_) {
        return fallback;
    }
}

export function rememberShoppingReturn(fromPath, toPath) {
    const toBase = toPath.split('?')[0];
    const fromBase = fromPath.split('?')[0];
    if (toBase === '/cart' && fromPath && !CART_FLOW.has(fromBase)) {
        stashShoppingReturnUrl(fromPath);
    }
}

export function pop() {
    window.history.back()
}
export function startViewTransition() {
    return new Promise((resolve) => {
        document.startViewTransition(() => {
            resolve();
        });
    });
}
export const successToast = async (msg, time = 5000) => {
    toasts.add({
        title: msg,
        description: "",
        duration: time,
        type: "success",
    });
};

export const errorToast = async (msg) => {
    toasts.add({
        title: msg,
        description: "",
        duration: 5000,
        type: "error",
    });
};

export const warningToast = async (msg, time = 4000) => {
    toasts.add({
        title: msg,
        description: "",
        duration: time,
        type: "warning",
    });
};

export const searchList = function (list, term) {
    if (!term) return list;
    return list.filter((el) => Object.values(el).filter(util).length > 0);

    function util(item) {
        let result = JSON.stringify(item || "");
        result = result.toLowerCase().replaceAll('"', "");
        return result.search(term.toLowerCase()) >= 0;
    }
}